import { computed } from 'vue'
import { useCartStore, useSubscriptionsStore } from '@/stores'
import type { Subscription } from '@/types'
import { domainFqdn } from '@/utils'

const isMailSubscription = (sub: Subscription): boolean =>
  (sub.product?.features as Record<string, unknown> | undefined)?.type === 'mail'

/**
 * Domains the account already holds that could still get email hosting.
 *
 * The order forms validate the shape of a hand-typed domain, never ownership,
 * so a typo quietly buys mail for a domain the account does not have. Offering
 * the account's own domains removes the typing from the common case.
 */
export function useMailDomainSuggestions() {
  const subscriptionsStore = useSubscriptionsStore()
  const cartStore = useCartStore()

  const suggestions = computed<string[]>(() => {
    const withMail = new Set<string>()
    const owned = new Set<string>()

    for (const sub of subscriptionsStore.getSubscriptions()) {
      const name = domainFqdn(sub.domain)
      if (!name) continue
      // A mail subscription counts whatever its state, so a domain mid-cancellation
      // is not offered again; only active registrations are worth suggesting.
      if (isMailSubscription(sub)) withMail.add(name)
      else if (sub.status === 'active') owned.add(name)
    }

    const inCart = new Set(
      cartStore.getCart.items
        .filter((item) => item.product === 'mail-domain')
        .map((item) => String(item.meta_data?.domain ?? ''))
    )

    return [...owned].filter((name) => !withMail.has(name) && !inCart.has(name)).sort()
  })

  /** Suggestions are an enhancement, so a failed fetch stays silent. */
  const loadSuggestions = () => {
    subscriptionsStore.fetchSubscriptions().catch(() => {})
  }

  return { suggestions, loadSuggestions }
}
